<div
  class="fi-trigger"
  tabindex="0"
  role="button"
  matTooltip="Open the ticketing tool guide for support &amp; operations"
  aria-label="How the AIS-140 Ticketing Tool Works"
  (click)="open()"
  (keydown.enter)="open()"
>
  <mat-icon class="fi-trigger-icon">menu_book</mat-icon>
</div>

<div
  class="custom-container ticketing-info-root info-panel-root"
  [class.active]="isOpen"
>
  <div class="custom-modal custom-modal-l">
    <div class="custom-modal-dialog">
      <div class="custom-modal-content">
        <div class="custom-modal-header">
          <h4 class="custom-modal-title">How the AIS-140 Ticketing Tool Works</h4>
          <div class="fi-header-actions">
            <button
              type="button"
              class="fi-export-btn"
              (click)="exportPdf()"
              aria-label="Export as PDF"
            >
              <mat-icon>picture_as_pdf</mat-icon>
              Export as PDF
            </button>
            <button type="button" class="custom-close-btn" (click)="close()">
              &times;
            </button>
          </div>
        </div>

        <div class="custom-modal-body fi-body">
          <p class="fi-intro">
            Every vehicle fitted with an Accolade tracking device has to pass
            government AIS-140 certification before it's road-legal. This
            tool tracks that certification, vehicle by vehicle, from the
            moment Tata Motors tells us a new vehicle exists to the moment
            its certificate is issued &mdash; it's what you're looking at
            when you open a ticket on the AIS 140 Tickets screen. This guide
            explains what happens behind the scenes so you can answer
            &ldquo;why does this ticket look like this?&rdquo; without
            guessing, using the same status codes and screen names you see
            in the app.
          </p>

          <section class="fi-section" id="ti-overview">
            <h5 class="fi-h5">1. What this tool does</h5>
            <p class="fi-p">
              One <strong>ticket</strong> tracks one vehicle's certification
              journey, end to end. A ticket is opened automatically the
              moment Tata Motors' system confirms a new vehicle has an
              Accolade device fitted. From there, the vehicle moves through
              four fixed <strong>stages</strong> of checks &mdash; device
              readiness, government registration, AIS-140 backend
              certification, and finally certificate generation &mdash;
              until it's marked <span class="fi-pill fi-pill-ok">Completed</span>.
              Along the way, this tool keeps Tata Motors' own systems
              updated automatically, and gives you the screens to check on a
              vehicle, update its status, or fix a problem.
            </p>
            <div class="fi-callout">
              <strong>Two words you'll see everywhere:</strong> a
              <strong>ticket</strong> is one vehicle's certification record
              (e.g. <code>AEPL-260918-0042</code>); a <strong>stage</strong>
              is one of the four fixed phases every ticket passes through
              (Section 4) &mdash; don't confuse this with the ticket's
              overall <em>status</em>, a separate field covered in Section 4
              and the status-code reference at the end.
            </div>
          </section>

          <section class="fi-section" id="ti-sources">
            <h5 class="fi-h5">2. Where a ticket comes from</h5>
            <p class="fi-p">
              Every ticket starts from one of two sources &mdash; but once it
              exists, both are treated identically.
            </p>

            <figure class="fi-figure">
              <svg
                viewBox="0 0 720 260"
                xmlns="http://www.w3.org/2000/svg"
                role="img"
                aria-label="Two ticket sources - Tata Motors' automatic feed, and a manual Institutional Sales spreadsheet upload - both feeding into the same ticket creation and validation pipeline."
              >
                <defs>
                  <marker id="tiArrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                    <path d="M0,0 L10,5 L0,10 z" fill="currentColor" />
                  </marker>
                </defs>
                <rect x="20" y="20" width="280" height="64" rx="10" fill="var(--theme-surface-alt)" stroke="var(--theme-border)" />
                <text x="160" y="46" text-anchor="middle" font-size="10.1" font-weight="600" fill="var(--theme-text)">Tata Motors' system</text>
                <text x="160" y="64" text-anchor="middle" font-size="9.0" fill="var(--theme-text-muted)">sends new/updated vehicle data automatically</text>

                <rect x="420" y="20" width="280" height="64" rx="10" fill="var(--theme-surface-alt)" stroke="var(--theme-border)" />
                <text x="560" y="42" text-anchor="middle" font-size="10.1" font-weight="600" fill="var(--theme-text)">Institutional Sales upload</text>
                <text x="560" y="60" text-anchor="middle" font-size="9.0" fill="var(--theme-text-muted)">a person uploads an Excel sheet</text>
                <text x="560" y="76" text-anchor="middle" font-size="8.6" fill="var(--theme-text-muted)">for fleet/institutional vehicles</text>

                <line x1="160" y1="84" x2="160" y2="106" stroke="currentColor" stroke-width="1.6" marker-end="url(#tiArrow)" />
                <line x1="560" y1="84" x2="560" y2="106" stroke="currentColor" stroke-width="1.6" marker-end="url(#tiArrow)" />
                <line x1="160" y1="106" x2="560" y2="106" stroke="currentColor" stroke-width="1.6" />
                <line x1="360" y1="106" x2="360" y2="128" stroke="currentColor" stroke-width="1.8" marker-end="url(#tiArrow)" />

                <rect x="180" y="130" width="360" height="56" rx="10" fill="var(--info-accent-fill)" stroke="var(--info-accent)" stroke-width="1.6" />
                <text x="360" y="153" text-anchor="middle" font-size="10.5" font-weight="700" fill="var(--info-accent-strong)">Same validation, same rules, every time</text>
                <text x="360" y="171" text-anchor="middle" font-size="9.0" fill="var(--info-accent-strong)">see Section 3</text>

                <line x1="360" y1="186" x2="360" y2="208" stroke="var(--info-ok)" stroke-width="1.8" marker-end="url(#tiArrow)" />

                <rect x="210" y="210" width="300" height="46" rx="10" fill="var(--info-ok-fill)" stroke="var(--info-ok)" stroke-width="1.6" />
                <text x="360" y="238" text-anchor="middle" font-size="10.5" font-weight="700" fill="var(--info-ok)">Ticket created &mdash; Stage 1</text>
              </svg>
              <figcaption class="fi-caption">
                Both sources hand data to the exact same checking process.
                The only practical difference is a label: tickets from an
                Institutional Sales upload are tagged so they can be
                filtered on the dashboard &mdash; nothing about how they're
                checked or processed is different.
              </figcaption>
            </figure>

            <p class="fi-p">
              <strong>Tata Motors' automatic feed</strong> is how the vast
              majority of tickets are created. Tata Motors' own FleetEdge system
              pushes vehicle details to us the moment a vehicle with an
              Accolade device is invoiced or registered &mdash; no one has to
              do anything, the ticket just appears, already assigned. On the
              dashboard and reports this shows up as
              <strong>TML CRM &amp; FE</strong>.
            </p>
            <p class="fi-p">
              <strong>Institutional Sales (manual upload)</strong> covers
              vehicles &mdash; typically fleet or institutional buyers
              &mdash; that aren't fed through Tata Motors' live system. A
              person fills in a spreadsheet (a downloadable template is
              provided on the Institutional Sales page) and uploads it. Each
              row becomes its own ticket, checked against the exact same
              rules as anything coming from Tata Motors.
            </p>
            <ul class="fi-rule-list">
              <li>Only <code>.xlsx</code> files are accepted, and the page asks you to keep uploads to roughly 500 rows at a time for performance.</li>
              <li>After uploading, you get a results table showing which rows became tickets and which failed &mdash; downloadable as a report to send back to whoever prepared the sheet.</li>
              <li>This is currently the <strong>only</strong> manual bulk-upload option in the app. There's no separate upload for ordinary retail vehicles &mdash; if a batch of normal vehicles needs to go in by hand, it still goes through this page and gets tagged Institutional Sales either way.</li>
            </ul>
          </section>

          <section class="fi-section" id="ti-validation">
            <h5 class="fi-h5">3. What gets checked before a ticket is accepted</h5>
            <p class="fi-p">
              Every field is checked before anything is saved &mdash; and
              it's all or nothing.
            </p>
            <div class="fi-callout-danger">
              <strong>All-or-nothing rule:</strong> if even one required
              field is missing or invalid, the <strong>entire</strong>
              incoming record is rejected &mdash; nothing is partially
              saved. Whoever sent the data gets back a full list of every
              problem found, so everything can be fixed in one pass instead
              of trickling in error by error.
            </div>

            <p class="fi-p"><strong>What has to be present and correct:</strong></p>
            <ul class="fi-rule-list">
              <li><strong>Chassis number (VIN)</strong> &mdash; must be a genuine Tata Motors VIN format (starts with <code>MAT</code>, 17 characters total).</li>
              <li><strong>Device details (ICCID, UID, IMEI, make, model)</strong> &mdash; the device must already be a known Accolade device on our own records, the make must be Accolade, and the ICCID/UID/IMEI must all match each other on file. A SIM (ICCID) that doesn't match the IMEI we have on record for it is rejected as a mismatch.</li>
              <li><strong>Engine number and vehicle registration number.</strong></li>
              <li><strong>Owner's last name, city, and first address line.</strong></li>
              <li><strong>Registered mobile number</strong> &mdash; exactly 10 digits.</li>
              <li><strong>Dealer code.</strong></li>
              <li><strong>RTO office code and RTO state</strong> &mdash; the state must be one this system already recognizes.</li>
              <li><strong>Primary and secondary operator + mobile number</strong> &mdash; each mobile number must be exactly 10 or 15 digits.</li>
              <li><strong>Vehicle model, manufacturing year, invoice date.</strong></li>
              <li><strong>Certificate validity duration</strong> &mdash; a number of years the certificate will be valid for, not a date.</li>
            </ul>

            <p class="fi-p">
              <strong>Not currently checked:</strong> owner's first/middle
              name, second address line, POS code, identity-document fields,
              alternate mobile number, and owner email are accepted as-is
              with no format checking right now &mdash; if any of these look
              wrong on a ticket, that's expected, not a bug.
            </p>

            <div class="fi-callout">
              <strong>Exact repeats are refused, not duplicated.</strong> If
              Tata Motors (or a re-uploaded sheet) sends data for a vehicle
              that already has an open ticket and <em>every</em> field it
              sends is identical to what's on file, the system recognizes it
              as a repeat. No new ticket is created and nothing changes; the
              sender gets back the existing ticket number with the message
              &ldquo;Duplicate entry found. Ticket Already in process.&rdquo;
            </div>
            <p class="fi-p">
              <strong>One request per vehicle at a time.</strong> If two
              requests for the same VIN arrive at the same moment, the
              system processes them one after the other, never in parallel.
              Two near-simultaneous requests therefore can't both open a
              ticket for the same vehicle.
            </p>
          </section>

          <section class="fi-section" id="ti-stages">
            <h5 class="fi-h5">4. The four certification stages</h5>
            <p class="fi-p">
              Every ticket walks through the same four stages, in order,
              each with its own checklist.
            </p>
            <div class="fi-table-wrap">
              <table class="fi-table">
                <thead>
                  <tr><th>Stage</th><th>What it's checking</th><th>Checklist items</th></tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>Stage 1</strong><br />Device Readiness</td>
                    <td>Is the device physically working on the vehicle?</td>
                    <td>Vehicle ignition on &middot; GSM network available &middot; GPS fix available</td>
                  </tr>
                  <tr>
                    <td><strong>Stage 2</strong><br />Vahan Certification</td>
                    <td>Is the vehicle recognized on the government's Vahan portal?</td>
                    <td>Vahan portal available &middot; vehicle's data present on Vahan</td>
                  </tr>
                  <tr>
                    <td><strong>Stage 3</strong><br />AIS-140 Certification</td>
                    <td>Do the safety features actually work end-to-end?</td>
                    <td>Backend portal available &middot; panic button event confirmed &middot; OTP / live-location fetching confirmed</td>
                  </tr>
                  <tr>
                    <td><strong>Stage 4</strong><br />Certificate Generation</td>
                    <td>Issuing the final certificate.</td>
                    <td>Certificate generated and submitted</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p class="fi-p">
              Each checklist item has its own status (in progress, on hold
              for a specific reason, or done) &mdash; see the status-code
              reference at the end. The ticket's overall status is separate
              from these per-item statuses; a ticket only reaches the final
              <span class="fi-pill fi-pill-ok">Completed</span> status once
              every item in all four stages is done (Section 8).
            </p>
            <div class="fi-callout">
              <strong>Skipping Stage 2:</strong> there's a deliberate
              override on the ticket-details page for when the vehicle's
              firmware update (FOTA) is taking too long, or hasn't even
              started, but the handler already knows the device's current
              firmware version and IP/network settings will work fine even
              without that update going through. In that case the handler
              marks that as the reason, and the restriction holding the
              ticket back is lifted so it can move forward. The restriction
              is the Stage 2 lock described in Section 5, which normally waits
              for the firmware update and the server-address settings. It
              requires typing in the reason first &mdash; if a ticket jumped
              past Stage 2, check what was typed there; it's an audited,
              intentional override, not an error.
            </div>
          </section>

          <section class="fi-section" id="ti-device-fota">
            <h5 class="fi-h5">5. What a new ticket does to the device: state, VIN &amp; firmware update</h5>
            <p class="fi-p">
              Each time a ticket is created, the system also updates the
              tracking device's own record and decides whether the device
              needs a firmware update (FOTA) before certification can go on.
              This covers brand-new tickets, change-request replacements,
              renewals, and a <code>STS_CO_19</code> ticket receiving its
              data for the first time. It applies to both 4G and 2G devices.
            </p>

            <figure class="fi-figure">
              <svg
                viewBox="0 0 720 400"
                xmlns="http://www.w3.org/2000/svg"
                role="img"
                aria-label="When a ticket is created, the device is always linked to the ticket's VIN. If the device has no state yet, it gets the ticket's RTO state; if it already has one, it keeps it. That state is then checked against the FOTA-not-required list: if listed, no firmware update is needed and Stage 2 is unlocked automatically; if not, an automatic FOTA batch is created and Stage 2 unlocks once the update is completed or skipped and both server addresses are set."
              >
                <defs>
                  <marker id="tiArrow3" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                    <path d="M0,0 L10,5 L0,10 z" fill="currentColor" />
                  </marker>
                  <marker id="tiArrowOk3" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                    <path d="M0,0 L10,5 L0,10 z" fill="var(--info-ok)" />
                  </marker>
                  <marker id="tiArrowAccent3" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                    <path d="M0,0 L10,5 L0,10 z" fill="var(--info-accent)" />
                  </marker>
                </defs>

                <rect x="210" y="20" width="300" height="46" rx="10" fill="var(--theme-surface-alt)" stroke="var(--theme-border)" />
                <text x="360" y="40" text-anchor="middle" font-size="9.8" font-weight="600" fill="var(--theme-text)">Ticket created</text>
                <text x="360" y="56" text-anchor="middle" font-size="8.2" fill="var(--theme-text-muted)">device's VIN is always set to the ticket's VIN</text>

                <line x1="360" y1="66" x2="360" y2="86" stroke="currentColor" stroke-width="1.6" marker-end="url(#tiArrow3)" />

                <polygon points="360,88 470,130 360,172 250,130" fill="var(--theme-surface)" stroke="var(--theme-border)" />
                <text x="360" y="126" text-anchor="middle" font-size="8.6" fill="var(--theme-text)">Does the device already</text>
                <text x="360" y="140" text-anchor="middle" font-size="8.6" fill="var(--theme-text)">have a state?</text>

                <line x1="250" y1="130" x2="232" y2="130" stroke="currentColor" stroke-width="1.4" marker-end="url(#tiArrow3)" />
                <text x="241" y="120" text-anchor="middle" font-size="8.2" fill="var(--theme-text-muted)">no</text>
                <rect x="20" y="105" width="210" height="50" rx="10" fill="var(--theme-surface-alt)" stroke="var(--theme-border)" />
                <text x="125" y="126" text-anchor="middle" font-size="8.8" font-weight="600" fill="var(--theme-text)">Assign the ticket's RTO state</text>
                <text x="125" y="141" text-anchor="middle" font-size="8.2" fill="var(--theme-text-muted)">to the device</text>

                <line x1="470" y1="130" x2="488" y2="130" stroke="currentColor" stroke-width="1.4" marker-end="url(#tiArrow3)" />
                <text x="479" y="120" text-anchor="middle" font-size="8.2" fill="var(--theme-text-muted)">yes</text>
                <rect x="490" y="105" width="210" height="50" rx="10" fill="var(--theme-surface-alt)" stroke="var(--theme-border)" />
                <text x="595" y="126" text-anchor="middle" font-size="8.8" font-weight="600" fill="var(--theme-text)">Keep the device's own state</text>
                <text x="595" y="141" text-anchor="middle" font-size="8.2" fill="var(--theme-text-muted)">ticket's RTO state not applied</text>

                <line x1="125" y1="155" x2="125" y2="200" stroke="currentColor" stroke-width="1.6" />
                <line x1="595" y1="155" x2="595" y2="200" stroke="currentColor" stroke-width="1.6" />
                <line x1="125" y1="200" x2="595" y2="200" stroke="currentColor" stroke-width="1.6" />
                <line x1="360" y1="200" x2="360" y2="224" stroke="currentColor" stroke-width="1.8" marker-end="url(#tiArrow3)" />

                <polygon points="360,226 470,270 360,314 250,270" fill="var(--theme-surface)" stroke="var(--theme-border)" />
                <text x="360" y="266" text-anchor="middle" font-size="8.6" fill="var(--theme-text)">Is that state on the</text>
                <text x="360" y="280" text-anchor="middle" font-size="8.6" fill="var(--theme-text)">FOTA-not-required list?</text>

                <line x1="250" y1="270" x2="232" y2="270" stroke="var(--info-ok)" stroke-width="1.6" marker-end="url(#tiArrowOk3)" />
                <text x="241" y="260" text-anchor="middle" font-size="8.2" fill="var(--info-ok)">yes</text>
                <rect x="20" y="245" width="210" height="50" rx="10" fill="var(--info-ok-fill)" stroke="var(--info-ok)" stroke-width="1.6" />
                <text x="125" y="266" text-anchor="middle" font-size="9.0" font-weight="700" fill="var(--info-ok)">No firmware update</text>
                <text x="125" y="281" text-anchor="middle" font-size="8.2" fill="var(--info-ok)">Stage 2 unlocked automatically</text>

                <line x1="470" y1="270" x2="488" y2="270" stroke="var(--info-accent)" stroke-width="1.6" marker-end="url(#tiArrowAccent3)" />
                <text x="479" y="260" text-anchor="middle" font-size="8.2" fill="var(--info-accent-strong)">no</text>
                <rect x="490" y="245" width="210" height="50" rx="10" fill="var(--info-accent-fill)" stroke="var(--info-accent)" stroke-width="1.6" />
                <text x="595" y="266" text-anchor="middle" font-size="9.0" font-weight="700" fill="var(--info-accent-strong)">Auto FOTA batch created</text>
                <text x="595" y="281" text-anchor="middle" font-size="8.2" fill="var(--info-accent-strong)">latest firmware for that state</text>

                <line x1="595" y1="295" x2="595" y2="326" stroke="var(--info-accent)" stroke-width="1.6" marker-end="url(#tiArrowAccent3)" />

                <rect x="470" y="328" width="250" height="56" rx="10" fill="var(--theme-surface-alt)" stroke="var(--theme-border)" />
                <text x="595" y="349" text-anchor="middle" font-size="8.6" font-weight="600" fill="var(--theme-text)">Stage 2 unlocks when FOTA is</text>
                <text x="595" y="364" text-anchor="middle" font-size="8.6" font-weight="600" fill="var(--theme-text)">Completed/Skipped and both IPs are Set</text>
              </svg>
              <figcaption class="fi-caption">
                The state that decides FOTA is the <strong>device's</strong>
                state, which is not always the ticket's RTO state. If the device
                already had a state before the ticket arrived, it keeps it,
                and the firmware check and update both use that state.
              </figcaption>
            </figure>

            <p class="fi-p"><strong>VIN and state on the device record</strong></p>
            <ul class="fi-rule-list">
              <li><strong>VIN is always linked.</strong> The device record's chassis number is set to the ticket's VIN every time, replacing whatever was stored before. After a device swap, the new device carries the vehicle's VIN from then on.</li>
              <li><strong>State is only filled in if the device has none.</strong> A device with no state yet gets the ticket's RTO state. A device that already has a state keeps it, even if the ticket's RTO state is different. The ticket never silently moves a device to another state.</li>
              <li>The device's state decides which government server addresses it should report to and which firmware it should run. To move a device to the ticket's RTO state on purpose, use <strong>Device State Config</strong>.</li>
              <li>These updates don't create a Device System Activity Log entry. An entry only appears later, if the device checks in still reporting a different VIN or server address. See that page's own guide.</li>
            </ul>

            <p class="fi-p"><strong>Is a firmware update (FOTA) needed?</strong></p>
            <p class="fi-p">
              The system checks the device's state (or the ticket's RTO state,
              if the device had none) against an internally maintained list
              of states where a firmware push isn't required.
            </p>
            <ul class="fi-rule-list">
              <li><strong>State is on the list:</strong> no firmware update. The Stage 2 restriction is lifted automatically, and the ticket-details page doesn't show the Device FOTA Status panel at all.</li>
              <li><strong>State isn't on the list:</strong> an automatic single-device FOTA batch named <code>Auto FOTA for &lt;ticket number&gt;</code> is created on the 4G or 2G server. It pushes the latest firmware configured for that state. The batch is linked to the ticket, and its FOTA Batch ID, firmware and progress show in the Device FOTA Status panel on the ticket-details page.</li>
              <li><strong>Stage 2 stays locked until the update is done.</strong> The first Stage 2 item can only be worked on once the device's FOTA status is <em>Completed</em> or <em>Skipped</em> <strong>and</strong> both its primary and secondary government server addresses are reported as <em>Set</em>. A handler can lift this lock early with the Skip Stage 2 override (Section 4).</li>
              <li>The list can change over time, so the same vehicle model may behave differently in different states, or on different dates.</li>
            </ul>

            <div class="fi-callout">
              <strong>Firmware update on a replacement ticket.</strong> When a
              change request or renewal replaces a ticket, the new FOTA batch
              is linked to the old ticket's batch. If the device is the same,
              the new batch notes that it follows the old one. If the device
              was swapped (a device change), the old device is
              <strong>aborted</strong> from the old batch with the reason
              &ldquo;Device Change request received&rdquo;. The old batch then
              stops waiting on a device that's no longer on the vehicle.
            </div>
            <div class="fi-callout-danger">
              <strong>&ldquo;Something wrong with FOTA batch. Please contact
              Server Support&rdquo;</strong> on the ticket-details page means
              the ticket needs a firmware update but no batch got linked to
              it. Batch creation failing never blocks the ticket itself. The
              page tries once to create the missing batch for a 4G device. If
              the message keeps coming back, raise it with Server Support.
            </div>
          </section>

          <section class="fi-section" id="ti-change-requests">
            <h5 class="fi-h5">6. Change requests: when a ticket gets replaced, not edited</h5>
            <p class="fi-p">
              If something important about a vehicle changes after its
              ticket is already open, the system doesn't edit the ticket
              &mdash; it closes it and opens a brand new one. This is
              deliberate: AIS-140 and registration data is treated as
              something that shouldn't be quietly overwritten. Every
              meaningful change leaves a clean, traceable pair of records
              &mdash; the old ticket showing exactly what it looked like
              before, and a new ticket starting fresh with the corrected
              details.
            </p>

            <figure class="fi-figure">
              <svg
                viewBox="0 0 760 260"
                xmlns="http://www.w3.org/2000/svg"
                role="img"
                aria-label="New data arrives for a vehicle that already has a ticket. If a meaningful field changed, the old ticket is closed with a change-request status and a brand new ticket is created at Stage 1. If nothing meaningful changed, the request is rejected outright and the old ticket is left untouched."
              >
                <defs>
                  <marker id="tiArrow2" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                    <path d="M0,0 L10,5 L0,10 z" fill="currentColor" />
                  </marker>
                  <marker id="tiArrowDanger" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                    <path d="M0,0 L10,5 L0,10 z" fill="var(--info-danger)" />
                  </marker>
                  <marker id="tiArrowOk" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                    <path d="M0,0 L10,5 L0,10 z" fill="var(--info-ok)" />
                  </marker>
                </defs>

                <rect x="270" y="20" width="220" height="50" rx="10" fill="var(--theme-surface-alt)" stroke="var(--theme-border)" />
                <text x="380" y="42" text-anchor="middle" font-size="9.8" font-weight="600" fill="var(--theme-text)">Existing open ticket</text>
                <text x="380" y="58" text-anchor="middle" font-size="8.2" fill="var(--theme-text-muted)">e.g. old owner, old SIM</text>

                <line x1="380" y1="70" x2="380" y2="90" stroke="currentColor" stroke-width="1.6" marker-end="url(#tiArrow2)" />
                <text x="396" y="83" font-size="8.2" fill="var(--theme-text-muted)">new data</text>

                <polygon points="380,92 470,140 380,188 290,140" fill="var(--theme-surface)" stroke="var(--theme-border)" />
                <text x="380" y="136" text-anchor="middle" font-size="8.6" fill="var(--theme-text)">Device, owner, or</text>
                <text x="380" y="150" text-anchor="middle" font-size="8.6" fill="var(--theme-text)">RTO details changed?</text>

                <line x1="470" y1="140" x2="540" y2="140" stroke="var(--info-danger)" stroke-width="1.7" marker-end="url(#tiArrowDanger)" />
                <text x="505" y="130" text-anchor="middle" font-size="8.6" fill="var(--info-danger)">yes</text>

                <rect x="550" y="110" width="190" height="46" rx="10" fill="var(--info-danger-fill)" stroke="var(--info-danger)" />
                <text x="645" y="130" text-anchor="middle" font-size="9.4" font-weight="700" fill="var(--info-danger)">Old ticket closed</text>
                <text x="645" y="146" text-anchor="middle" font-size="8.2" fill="var(--info-danger)">status: Change Request (cancelled)</text>

                <line x1="645" y1="156" x2="645" y2="178" stroke="var(--info-ok)" stroke-width="1.7" marker-end="url(#tiArrowOk)" />

                <rect x="550" y="180" width="190" height="46" rx="10" fill="var(--info-ok-fill)" stroke="var(--info-ok)" />
                <text x="645" y="200" text-anchor="middle" font-size="9.4" font-weight="700" fill="var(--info-ok)">New ticket created</text>
                <text x="645" y="216" text-anchor="middle" font-size="8.2" fill="var(--info-ok)">fresh ticket number, starts at Stage 1</text>

                <line x1="290" y1="140" x2="212" y2="140" stroke="var(--info-danger)" stroke-width="1.4" marker-end="url(#tiArrowDanger)" />
                <text x="251" y="130" text-anchor="middle" font-size="8.6" fill="var(--info-danger)">no</text>

                <rect x="20" y="115" width="190" height="50" rx="10" fill="var(--info-danger-fill)" stroke="var(--info-danger)" />
                <text x="115" y="135" text-anchor="middle" font-size="9.4" font-weight="700" fill="var(--info-danger)">Rejected outright</text>
                <text x="115" y="151" text-anchor="middle" font-size="8.2" fill="var(--info-danger)">old ticket untouched, nothing saved</text>
              </svg>
              <figcaption class="fi-caption">
                A meaningful field change closes the old ticket and opens a
                new one. If nothing meaningful changed, the incoming data is
                <strong>rejected outright</strong> and the existing ticket is
                left exactly as it was &mdash; it is <em>not</em> updated
                with whatever else was in the request. If the incoming data
                is identical to what's already on file, nothing happens at
                all (Section 3).
              </figcaption>
            </figure>

            <p class="fi-p"><strong>The three kinds of change request:</strong></p>
            <div class="fi-table-wrap">
              <table class="fi-table">
                <thead>
                  <tr><th>What changed</th><th>Real-world reason</th><th>Old ticket's new status</th></tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>Device / SIM change</strong><br />ICCID, IMEI, UID, or the mobile operator/number on file</td>
                    <td>The tracking device or SIM was physically swapped &mdash; e.g. a faulty unit replaced, or the wrong device details were originally submitted.</td>
                    <td><code>STS_CO_20</code><br />Cancelled &ndash; Device Change</td>
                  </tr>
                  <tr>
                    <td><strong>Owner change</strong><br />Last name, city, address, mobile, or email</td>
                    <td>The vehicle was sold or re-registered to someone else, or the customer's contact details were corrected.</td>
                    <td><code>STS_CO_21</code><br />Cancelled &ndash; Owner Change</td>
                  </tr>
                  <tr>
                    <td><strong>RTO / State change</strong><br />RTO office code or state</td>
                    <td>The vehicle ended up registered in a different state or RTO office than originally submitted.</td>
                    <td><code>STS_CO_22</code><br />Cancelled &ndash; RTO/State Change</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <ul class="fi-rule-list">
              <li>The new ticket keeps the old ticket's <strong>certificate validity duration</strong>. Everything else comes fresh from the new data.</li>
              <li><strong>Who it's assigned to:</strong> for a device or owner change, the new ticket stays with the old ticket's assignee. If that person has since been deactivated, or the old ticket was never assigned, it's re-assigned by RTO state and dealer. For an RTO/state change, it's always re-assigned to whoever handles the new state.</li>
              <li>If the vehicle has <strong>other</strong> tickets still open (left over from older history), they're closed together with the main one, with the same cancellation status, so no orphaned ticket is left open.</li>
              <li>The new ticket goes through the device and firmware-update steps in Section 5 again, with its FOTA batch linked to the old one.</li>
              <li>There's <strong>no limit</strong> on how many times a vehicle's ticket can be replaced this way &mdash; if a vehicle changes hands twice, you'll see two change-request cycles in its history.</li>
              <li>If incoming data is different from what's on file but doesn't touch any of these three areas, it's <strong>rejected outright</strong> rather than accepted or treated as a change request &mdash; the ticket is left untouched.</li>
              <li><strong>One exception:</strong> a ticket sitting at <code>STS_CO_19</code> (Ticket Generated but Data Not Received from CRM) is <strong>updated in place</strong> with the incoming data instead of being rejected. If it hadn't properly started yet, it starts now: assignment date, device state/VIN and FOTA (Section 5) all happen at this point.</li>
            </ul>
          </section>

          <section class="fi-section" id="ti-expiry">
            <h5 class="fi-h5">7. Certificate renewal: what happens once Stage 2 is done</h5>
            <p class="fi-p">
              Once a vehicle has passed Vahan certification (Stage 2), its
              core details are treated as locked in &mdash; new data can no
              longer just edit it. A support agent marks Stage 2 complete on
              the ticket-details page (a manual step, not automatic). From
              then on, if Tata Motors sends new data for that vehicle, one of
              two things happens depending on how old that ticket is:
            </p>
            <ul class="fi-rule-list">
              <li>
                <strong>Ticket is 3 months old or newer &rarr; rejected.</strong>
                The new data is refused outright with a message that changes
                can't be made after Stage 2. Nothing happens automatically
                &mdash; this needs a person to look into why new data is
                arriving for an already-progressing certification.
              </li>
              <li>
                <strong>Ticket is older than 3 months &rarr; treated as a renewal.</strong>
                The old ticket is automatically closed (as Completed if it
                was waiting on RTO approval, otherwise as Cancelled), and a
                brand new ticket is created from scratch, starting again at
                Stage 1. Any other still-open tickets for the vehicle are
                closed the same way. The new ticket is assigned fresh by RTO
                state and dealer.
              </li>
            </ul>
            <div class="fi-callout">
              <strong>Does the renewed ticket need a firmware update first?</strong>
              It follows the same rule as every new ticket (Section 5). The
              device keeps its existing state, that state is checked against
              the FOTA-not-required list, and Stage 2 stays locked until any
              required update is done.
            </div>
          </section>

          <section class="fi-section" id="ti-completing">
            <h5 class="fi-h5">8. Closing a ticket as Completed</h5>
            <p class="fi-p">
              A ticket can only close as Completed once every checklist item
              is done and at least one certificate is attached &mdash; with
              one exception, noted below.
            </p>

            <p class="fi-p"><strong>Where a ticket can be marked Completed:</strong></p>
            <ul class="fi-rule-list">
              <li><strong>Ticket Details page</strong> &mdash; working through the stage checklist yourself, one item at a time, until the last one is ticked off.</li>
              <li><strong>Update Ticket popup</strong> (single ticket, from the ticket list) &mdash; set the status to Completed directly, with a certificate upload.</li>
              <li><strong>Bulk Update popup</strong> (many tickets at once, from the ticket list) &mdash; the same action applied to a batch, with certificates for each vehicle uploaded together and auto-matched by filename.</li>
              <li><strong>A SharePoint certificate folder, watched automatically</strong> &mdash; certificate PDFs dropped into a shared SharePoint folder are picked up by an automatic check every 5 minutes, matched to a ticket by the VIN in the filename (the same naming rule as a manual upload), and that ticket is completed the same way a Bulk Update would. Processed files are moved into a &ldquo;processed&rdquo; folder; anything that couldn't be matched or failed lands in a &ldquo;failed&rdquo; folder for someone to check.</li>
              <li><strong>An external automation calling the AEPL ticketing API directly</strong> &mdash; a separate integration endpoint lets an outside system mark a ticket Completed by ticket number (one at a time or as a batch) without going through the ticket-list screens at all.</li>
            </ul>

            <p class="fi-p">
              <strong>The certificate requirement</strong> (for every path
              above except the external automation one): to move a ticket to
              <span class="fi-pill fi-pill-ok">STS_CO_18 Completed</span>, a
              certificate file has to be uploaded, and it has to be a PDF
              whose filename contains the vehicle's chassis number (VIN)
              &mdash; that's how the system knows which vehicle a given file
              belongs to. When completing many tickets at once, you can
              upload many PDFs together and the system sorts them out by
              matching each filename to a ticket automatically.
            </p>
            <ul class="fi-rule-list">
              <li><strong>Vahan certificate</strong> &mdash; the certificate for the government Vahan/VLTD registry (Stage 2's authority). Recognized by the word &ldquo;vltd&rdquo; in the filename, or a filename that's just the VIN itself.</li>
              <li><strong>Backend certificate</strong> &mdash; the AIS-140 backend-platform certificate (Stage 3's authority). This is the default if the filename doesn't match the Vahan pattern above.</li>
              <li><strong>Other certificates</strong> &mdash; a catch-all slot for any additional supporting documents beyond the two required certificates.</li>
            </ul>
            <p class="fi-p">
              Only <strong>one</strong> of these three needs to be present to
              satisfy the completion requirement &mdash; not all three.
            </p>
            <div class="fi-callout-ok">
              <strong>Completing fast-forwards the checklist.</strong> When a
              ticket is moved straight to Completed, the system
              automatically marks every remaining stage/checklist item as
              done too &mdash; you don't have to manually tick every earlier
              box first.
            </div>
            <p class="fi-p">
              A Master Access user can re-attach or replace a certificate on
              a ticket that's <strong>already</strong> Completed, if a
              corrected file needs to go in after the fact, without
              re-running the whole completion process.
            </p>
          </section>

          <section class="fi-section" id="ti-tat">
            <h5 class="fi-h5">9. Turnaround time (TAT) tracking</h5>
            <p class="fi-p">
              Every ticket tracks how long it's taking &mdash; fairly, by not
              counting time we were stuck waiting on someone else.
            </p>
            <ul class="fi-rule-list">
              <li><strong>Total time</strong> &mdash; simple wall-clock time since the ticket opened.</li>
              <li><strong>Effective time</strong> &mdash; total time minus any time the ticket spent on hold.</li>
              <li><strong>Business time</strong> &mdash; effective time counted only during working hours: <strong>9:00 AM&ndash;5:30 PM, Monday to Saturday.</strong> Sundays don't count at all; once a ticket closes, it's bucketed as under 4 business-hours, under 10, or over 10.</li>
            </ul>
            <p class="fi-p">
              <strong>What puts a ticket &ldquo;on hold&rdquo;:</strong>
              various on-hold statuses pause the TAT clock. Waiting on
              <strong>RTO approval</strong> and a <strong>suspended /
              customer not responding</strong> ticket each get their own TAT
              category, so reporting can tell &ldquo;we're slow&rdquo; apart
              from &ldquo;we're waiting on someone outside the
              company.&rdquo; A ticket left open overnight or outside
              working hours is automatically put on hold until the next
              working period begins, so no one is penalized for the clock
              running while the office is closed.
            </p>
          </section>

          <section class="fi-section" id="ti-sync">
            <h5 class="fi-h5">10. Keeping Tata Motors in sync</h5>
            <p class="fi-p">
              Every time a ticket's stage or status changes, Tata Motors'
              systems are automatically told &mdash; on two separate
              channels, at the same time. This happens for every status
              change, whether a person made it or an automated process did,
              and it never blocks or delays the change itself &mdash; if the
              notification fails to send, the ticket update still goes
              through, and the failure is logged for later investigation.
            </p>
            <div class="fi-callout">
              <strong>If Tata Motors says they never received an update:</strong>
              use <strong>Resend Ticket Status</strong> on the ticket-list
              screen's options menu. Filter down to the affected tickets (by
              date, dealer, state, etc. &mdash; at least one filter is
              required) and it will re-send each ticket's current status on
              both channels, without changing anything in the ticket's
              history.
            </div>
            <p class="fi-p">
              Separately, for older-generation (2G) devices, a ticket
              reaching Completed also automatically forwards its record to a
              sibling system that manages those devices, so that system's
              own records stay current too.
            </p>
          </section>

          <section class="fi-section" id="ti-app">
            <h5 class="fi-h5">11. What you can do in the app</h5>

            <p class="fi-p"><strong>The ticket list screen</strong></p>
            <ul class="fi-rule-list">
              <li>KPI shortcuts at the top (All / In Progress / On Hold / Cancelled / Completed) double as one-click filters.</li>
              <li>A full filter panel &mdash; date range, RTO state, dealer, assigned user, exact status, source (TML CRM &amp; FE vs. Institutional Sales), device type, TAT bucket, and free-text search across VIN/IMEI/ICCID/ticket number.</li>
              <li><strong>Update Ticket</strong> (single) and <strong>Bulk Update</strong> (many at once, with per-vehicle certificate matching) for changing status.</li>
              <li><strong>Assign To</strong> &mdash; hand a ticket (or several) to a different person.</li>
              <li><strong>Download Report</strong> and <strong>Resend Ticket Status</strong> in the options menu.</li>
            </ul>

            <p class="fi-p"><strong>The ticket details screen</strong></p>
            <ul class="fi-rule-list">
              <li>The full four-stage checklist, with each item's own status and remark.</li>
              <li>Skip Stage 2 (with a required reason).</li>
              <li>Reassign just this one ticket.</li>
              <li><strong>Ticket History</strong> &mdash; every stage/status change ever made on this ticket, including <strong>who</strong> made it, or, for an automatic change, what triggered it &mdash; downloadable as a report.</li>
              <li>A direct link out to the government Vahan/VLTD portal for that vehicle.</li>
            </ul>

            <p class="fi-p"><strong>Permission levels</strong></p>
            <div class="fi-table-wrap">
              <table class="fi-table">
                <thead><tr><th>Level</th><th>Can see</th><th>Can do</th></tr></thead>
                <tbody>
                  <tr>
                    <td><strong>View access</strong></td>
                    <td>Only tickets assigned to you</td>
                    <td>Look up and review tickets</td>
                  </tr>
                  <tr>
                    <td><strong>Edit access</strong></td>
                    <td>Only tickets assigned to you</td>
                    <td>Everything above, plus update status and checklist items</td>
                  </tr>
                  <tr>
                    <td><strong>Master Access</strong></td>
                    <td><strong>Every</strong> ticket, company-wide</td>
                    <td>Everything above, plus reassign tickets between people, edit tickets already Cancelled or Completed, and access the admin-only Ticket Assignment and Dealer Master pages</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section class="fi-section" id="ti-gotchas">
            <h5 class="fi-h5">12. Good to know</h5>
            <ul class="fi-rule-list">
              <li>
                <strong>Stages aren't force-ordered on the checklist screen.</strong>
                Nothing technically stops you from marking a later stage
                complete before an earlier one &mdash; please still work
                through Stage 1 &rarr; 2 &rarr; 3 &rarr; 4 in order, since
                skipping ahead makes a ticket's history confusing for the
                next person who looks at it.
              </li>
              <li>
                <strong>Skip Stage 2 is an audited override, not a bug.</strong>
                If a ticket jumped past Stage 2, check the reason that was
                typed in &mdash; it's there on purpose.
              </li>
              <li>
                <strong>Reassigning a ticket keeps its real progress.</strong>
                Handing a ticket to someone else correctly preserves its
                current stage and status in the history &mdash; it won't
                look like the ticket &ldquo;went back to Stage 1&rdquo; just
                because it changed hands.
              </li>
              <li>
                <strong>History now shows who did what.</strong> Every entry
                in a ticket's history shows the person who made that change,
                or, for an automatic change (like the nightly hold or an
                update pushed straight from Tata Motors), a label describing
                what triggered it.
              </li>
              <li>
                <strong>The device's state can differ from the ticket's RTO
                state.</strong> That's expected when the device already had a
                state before the ticket arrived. A ticket only fills in a
                missing state and never overwrites one (Section 5). If the
                device really belongs to the ticket's state, change it on
                Device State Config.
              </li>
            </ul>
          </section>

          <section class="fi-section" id="ti-status-codes">
            <h5 class="fi-h5">Status code reference</h5>
            <p class="fi-p">
              The exact codes you'll see in the app and in reports, grouped
              by what they mean for a ticket.
            </p>

            <p class="fi-p"><strong>In progress</strong></p>
            <div class="fi-table-wrap">
              <table class="fi-table">
                <tbody>
                  <tr><td><code>STS_CO_01</code></td><td>Not Assigned</td></tr>
                  <tr><td><code>STS_CO_02</code></td><td>Assigned</td></tr>
                  <tr><td><code>STS_CO_03</code></td><td>Started</td></tr>
                  <tr><td><code>STS_CO_04</code></td><td>In Queue</td></tr>
                  <tr><td><code>STS_CO_05</code></td><td>In Progress</td></tr>
                  <tr><td><code>STS_CO_06</code></td><td>Completed <em>(a single checklist item &mdash; not the whole ticket, see below)</em></td></tr>
                </tbody>
              </table>
            </div>

            <p class="fi-p"><strong>On hold</strong></p>
            <div class="fi-table-wrap">
              <table class="fi-table">
                <tbody>
                  <tr><td><code>STS_CO_07</code></td><td>On Hold &ndash; Vehicle Not Available</td></tr>
                  <tr><td><code>STS_CO_08</code></td><td>On Hold &ndash; Ignition Not ON</td></tr>
                  <tr><td><code>STS_CO_09</code></td><td>On Hold &ndash; GSM Network Not Available</td></tr>
                  <tr><td><code>STS_CO_10</code></td><td>On Hold &ndash; Invalid GPS</td></tr>
                  <tr><td><code>STS_CO_11</code></td><td>On Hold &ndash; Vahan Portal Down</td></tr>
                  <tr><td><code>STS_CO_12</code></td><td>On Hold &ndash; Data Not Available on Vahan</td></tr>
                  <tr><td><code>STS_CO_13</code></td><td>On Hold &ndash; Backend Portal Down</td></tr>
                  <tr><td><code>STS_CO_14</code></td><td>On Hold &ndash; Panic Event Not Confirmed</td></tr>
                  <tr><td><code>STS_CO_15</code></td><td>On Hold &ndash; OTP Not Received</td></tr>
                  <tr><td><code>STS_CO_16</code></td><td>On Hold &ndash; Live Location Pending from Backend</td></tr>
                  <tr><td><code>STS_CO_17</code></td><td>On Hold &ndash; Waiting for RTO Approval</td></tr>
                  <tr><td><code>STS_CO_19</code></td><td>Ticket Generated but Data Not Received from CRM</td></tr>
                  <tr><td><code>STS_CO_25</code></td><td>Change Required from CRM</td></tr>
                  <tr><td><code>STS_CO_26</code></td><td>Other</td></tr>
                  <tr><td><code>STS_CO_28</code></td><td>On Hold &ndash; Non-Working Hours <em>(the automatic overnight/off-hours hold)</em></td></tr>
                  <tr><td><code>STS_CO_29</code></td><td>On Hold &ndash; SIM Validity Issue</td></tr>
                  <tr><td><code>STS_CO_30</code></td><td>Suspended &ndash; Customer Not Responding <em>(the automatic 60-day-no-response hold)</em></td></tr>
                  <tr><td><code>STS_CO_31</code></td><td>On Hold &ndash; No Response from Customer</td></tr>
                  <tr><td><code>STS_CO_32</code></td><td>On Hold &ndash; Customer Details Mismatch</td></tr>
                </tbody>
              </table>
            </div>

            <p class="fi-p"><strong>Cancelled</strong></p>
            <div class="fi-table-wrap">
              <table class="fi-table">
                <tbody>
                  <tr><td><code>STS_CO_20</code></td><td>Cancelled &ndash; Change Request: Device Change</td></tr>
                  <tr><td><code>STS_CO_21</code></td><td>Cancelled &ndash; Change Request: Vehicle Owner Change</td></tr>
                  <tr><td><code>STS_CO_22</code></td><td>Cancelled &ndash; Change Request: RTO/State Change</td></tr>
                  <tr><td><code>STS_CO_23</code></td><td>Cancelled as per Request</td></tr>
                  <tr><td><code>STS_CO_24</code></td><td>Cancelled &ndash; Invoice Cancelled</td></tr>
                </tbody>
              </table>
            </div>

            <p class="fi-p"><strong>Completed</strong></p>
            <div class="fi-table-wrap">
              <table class="fi-table">
                <tbody>
                  <tr><td><code>STS_CO_18</code></td><td><strong>Ticket Completed and Closed</strong> &mdash; the real &ldquo;done&rdquo; status for the whole ticket.</td></tr>
                </tbody>
              </table>
            </div>

            <div class="fi-callout">
              <strong>Don't confuse these two &ldquo;Completed&rdquo; codes:</strong>
              <span class="fi-pill fi-pill-ok">STS_CO_06</span> means one
              checklist item inside a stage is done.
              <span class="fi-pill fi-pill-ok">STS_CO_18</span> means the
              <strong>entire ticket</strong> is finished, certificates and
              all. A ticket can have plenty of STS_CO_06 items ticked off and
              still be nowhere near STS_CO_18.
            </div>
          </section>

          <section class="fi-section" id="ti-glossary">
            <h5 class="fi-h5">Glossary</h5>
            <div class="fi-glossary">
              <div class="fi-term">
                <dt>AIS-140</dt>
                <dd>The Indian government safety standard vehicles with a tracking device must be certified against.</dd>
              </div>
              <div class="fi-term">
                <dt>Vahan</dt>
                <dd>The government's national vehicle registration portal &mdash; Stage 2's authority.</dd>
              </div>
              <div class="fi-term">
                <dt>RTO</dt>
                <dd>Regional Transport Office &mdash; the government office where a vehicle is registered.</dd>
              </div>
              <div class="fi-term">
                <dt>VIN</dt>
                <dd>Vehicle Identification Number, a.k.a. chassis number &mdash; the unique 17-character code identifying the vehicle.</dd>
              </div>
              <div class="fi-term">
                <dt>ICCID</dt>
                <dd>The unique serial number printed on a SIM card.</dd>
              </div>
              <div class="fi-term">
                <dt>IMEI</dt>
                <dd>The unique serial number of the tracking device itself.</dd>
              </div>
              <div class="fi-term">
                <dt>UID</dt>
                <dd>Accolade's own internal identifier for a device.</dd>
              </div>
              <div class="fi-term">
                <dt>TML</dt>
                <dd>Tata Motors Limited &mdash; the vehicle manufacturer whose system feeds this tool and receives status updates back.</dd>
              </div>
              <div class="fi-term">
                <dt>AEPL</dt>
                <dd>Accolade Electronics Pvt. Ltd. &mdash; the company running this ticketing tool (also the prefix on every ticket number).</dd>
              </div>
              <div class="fi-term">
                <dt>TAT</dt>
                <dd>Turnaround Time &mdash; how long a ticket takes to close, see Section 9.</dd>
              </div>
              <div class="fi-term">
                <dt>Institutional Sales</dt>
                <dd>Vehicles (typically fleet/institutional buyers) whose ticket data is entered manually via spreadsheet rather than arriving automatically from Tata Motors.</dd>
              </div>
              <div class="fi-term">
                <dt>Change request</dt>
                <dd>What happens when a meaningful detail about an already-ticketed vehicle changes &mdash; see Section 6.</dd>
              </div>
              <div class="fi-term">
                <dt>FOTA</dt>
                <dd>Firmware Over The Air &mdash; a remote firmware update pushed to the device. A ticket raises one automatically when the device's state needs it; see Section 5.</dd>
              </div>
              <div class="fi-term">
                <dt>Device state</dt>
                <dd>The state stored on the device's own record. It decides which government server addresses and firmware the device should use, and can differ from the ticket's RTO state; see Section 5.</dd>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  </div>
</div>
